import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { getApiUrl } from "../config/api";

export const useCurrentContext = () => {
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();

  const [currentContext, setCurrentContext] = useState({
    type: "global",
    id: null,
    name: null,
    data: null,
    loading: false,
  });

  // Extract context from current path
  const getCurrentContext = () => {
    const path = location.pathname;

    // Organization context
    const orgMatch = path.match(/\/organization\/([^\/]+)/);
    if (orgMatch) {
      return { type: "organization", id: orgMatch[1] };
    }

    // Business unit context
    const buMatch = path.match(/\/business-unit\/([^\/]+)/);
    if (buMatch) {
      return { type: "businessUnit", id: buMatch[1] };
    }

    return { type: "global", id: null };
  };

  // Fetch entity data based on context
  const fetchEntityData = async (context) => {
    if (!context.id || !getAccessTokenSilently) return;

    try {
      setCurrentContext((prev) => ({ ...prev, loading: true }));
      let entityData = null;

      if (context.type === "organization") {
        const response = await fetch(getApiUrl(`organizations/${context.id}`), {
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        });
        if (response.ok) {
          entityData = await response.json();
        }
      } else if (context.type === "businessUnit") {
        const response = await fetch(
          getApiUrl(`business-units/${context.id}`),
          {
            headers: {
              Authorization: `Bearer ${await getAccessTokenSilently()}`,
            },
          }
        );
        if (response.ok) {
          entityData = await response.json();
        }
      }

      setCurrentContext({
        ...context,
        name: entityData?.name || entityData?.organization_name || "Unknown",
        data: entityData,
        loading: false,
      });
    } catch (error) {
      console.error("Error fetching entity data:", error);
      setCurrentContext({
        ...context,
        name: "Unknown",
        data: null,
        loading: false,
      });
    }
  };

  useEffect(() => {
    const context = getCurrentContext();
    if (
      context.type !== currentContext.type ||
      context.id !== currentContext.id
    ) {
      if (context.type === "global") {
        setCurrentContext({
          type: "global",
          id: null,
          name: null,
          data: null,
          loading: false,
        });
      } else {
        fetchEntityData(context);
      }
    }
  }, [location.pathname]);

  return {
    currentContext,
    isOrganizationContext: currentContext.type === "organization",
    isBusinessUnitContext: currentContext.type === "businessUnit",
    isGlobalContext: currentContext.type === "global",
    contextLoading: currentContext.loading,
  };
};
